import React, { useState, useContext, useEffect } from 'react';
import {
  Box, Text, Input, InputGroup, InputLeftElement, Table, Thead, Tbody, Tr, Th, Td,
  Button, Flex, Heading, Badge, Spinner, useToast, HStack, Select, IconButton,
  Tooltip, Card, CardHeader, CardBody, VStack, Tag
} from "@chakra-ui/react";
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon, TimeIcon, CheckIcon, WarningIcon } from '@chakra-ui/icons';
import { FaFilter } from 'react-icons/fa';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';
import ReactMarkdown from 'react-markdown';

function IONMCasesSimple() {
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  
  // State variables
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [timeFilter, setTimeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('needs_review'); // Default to needs_review
  const [pagination, setPagination] = useState({
    currentPage: 1,
    hasNext: false,
    hasPrev: false
  });
  
  // Fetch cases when filters change
  useEffect(() => {
    fetchCases();
  }, [accessToken, pagination.currentPage]);
  
  // Function to fetch cases from the API
  const fetchCases = async () => {
    if (!accessToken) return;
    
    setLoading(true);
    try {
      const response = await callApi('/getcases_simple', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page: pagination.currentPage,
          per_page: 20,
          search_term: searchTerm,
          hours: timeFilter ? parseInt(timeFilter) : null,
          status_filter: statusFilter
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setCases(data.cases || []);
        setPagination({
          currentPage: pagination.currentPage,
          hasNext: data.pagination?.has_next || false,
          hasPrev: data.pagination?.has_prev || false
        });
      } else {
        toast({
          title: 'Error fetching cases',
          description: data.error || 'Failed to fetch cases',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
    fetchCases();
  };
  
  // Handle time filter change
  const handleTimeFilterChange = (e) => {
    setTimeFilter(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
    // We'll fetch cases after state is updated
    setTimeout(() => fetchCases(), 0);
  };
  
  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
    // We'll fetch cases after state is updated
    setTimeout(() => fetchCases(), 0);
  };
  
  // Handle pagination
  const goToNextPage = () => {
    if (pagination.hasNext) {
      setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));
    }
  };
  
  const goToPrevPage = () => {
    if (pagination.hasPrev) {
      setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
    }
  };

  const handleQuickApprove = async (caseId, notes) => {
    if (!accessToken) return;
    
    // Show confirmation dialog
    if (!window.confirm("Are you sure you want to approve this case?")) {
      return;
    }
    
    try {
      const response = await callApi(`/coding/results/${caseId}/approve`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: `Quick approval: ${notes ? 'Based on notes' : 'No notes provided'}` })
      });
      
      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Case approved successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Refresh the case list
        fetchCases();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to approve case');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  // Get status badge color based on date
  const getStatusColor = (dateString) => {
    if (!dateString) return 'gray';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    
    if (diffHours < 24) return 'green';
    if (diffHours < 72) return 'yellow';
    return 'gray';
  };
  
  // Get coding status badge color and label
  const getCodingStatusInfo = (codingStatus) => {
    if (!codingStatus) {
      return { color: 'gray', label: 'Not Started', icon: null };
    }
    
    switch (codingStatus.status) {
      case 'approved':
        return { color: 'green', label: 'Approved', icon: <CheckIcon /> };
      case 'needs_review':
        return { color: 'yellow', label: 'Needs Review', icon: <WarningIcon /> };
      case 'completed':
        return { color: 'blue', label: 'Completed', icon: <CheckIcon /> };
      case 'error':
        return { color: 'red', label: 'Error', icon: <WarningIcon /> };
      case 'rerun':
        return { color: 'orange', label: 'Rerun', icon: <WarningIcon /> };
      case 'not_started':
      default:
        return { color: 'gray', label: 'Not Started', icon: null };
    }
  };
  
  return (
    <Box fontSize="md">
      <HeaderMenus />
      
      <Box p={4} maxW="1600px" mx="auto">
        <Card variant="outline" mb={4} boxShadow="sm">
          <CardHeader bg="blue.50" py={3}>
            <Heading size="md">IONM Cases</Heading>
          </CardHeader>
          <CardBody>
            <Flex direction={{ base: 'column', md: 'row' }} mb={4} justify="space-between" align="center">
              {/* Search Form */}
              <Box mb={{ base: 4, md: 0 }} width={{ base: '100%', md: 'auto' }}>
                <form onSubmit={handleSearch}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search cases..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      width={{ base: '100%', md: '300px' }}
                    />
                    <Button
                      ml={2}
                      colorScheme="blue"
                      type="submit"
                      isLoading={loading}
                    >
                      Search
                    </Button>
                  </InputGroup>
                </form>
              </Box>
              
              {/* Filters */}
              <HStack spacing={4}>
                {/* Status Filter */}
                <Tooltip label="Filter by status">
                  <Box>
                    <InputGroup size="md">
                      <InputLeftElement pointerEvents="none">
                        <FaFilter color="gray.400" />
                      </InputLeftElement>
                      <Select
                        placeholder="Status filter"
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        pl={10}
                      >
                        <option value="all">All statuses</option>
                        <option value="needs_review">Needs Review</option>
                        <option value="approved">Approved</option>
                        <option value="completed">Completed</option>
                        <option value="error">Error</option>
                        <option value="rerun">Rerun</option>
                        <option value="not_started">Not Started</option>
                      </Select>
                    </InputGroup>
                  </Box>
                </Tooltip>
                
                {/* Time Filter */}
                <Tooltip label="Filter by time">
                  <Box>
                    <InputGroup size="md">
                      <InputLeftElement pointerEvents="none">
                        <TimeIcon color="gray.400" />
                      </InputLeftElement>
                      <Select
                        placeholder="Time filter"
                        value={timeFilter}
                        onChange={handleTimeFilterChange}
                        pl={10}
                      >
                        <option value="">All time</option>
                        <option value="24">Last 24 hours</option>
                        <option value="72">Last 3 days</option>
                        <option value="168">Last week</option>
                        <option value="720">Last month</option>
                      </Select>
                    </InputGroup>
                  </Box>
                </Tooltip>
              </HStack>
            </Flex>
            
            {/* Cases Table */}
            <Box overflowX="auto">
              <Table variant="simple" size="sm" borderWidth="1px" borderRadius="md">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Case Information</Th>
                    <Th>Procedure</Th>
                    <Th>Dates</Th>
                    <Th>Coding Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {loading && cases.length === 0 ? (
                    <Tr>
                      <Td colSpan={4} textAlign="center" py={4}>
                        <Spinner size="md" color="blue.500" />
                        <Text mt={2}>Loading cases...</Text>
                      </Td>
                    </Tr>
                  ) : cases.length === 0 ? (
                    <Tr>
                      <Td colSpan={4} textAlign="center" py={4}>
                        <Text color="gray.500">No cases found</Text>
                      </Td>
                    </Tr>
                  ) : 
                  
                  (
                    cases.map((caseItem, index) => {
                      const codingStatusInfo = getCodingStatusInfo(caseItem.coding_status);
                      
                      return (
                        <Tr 
                          key={caseItem.case_id || index}
                          _hover={{ bg: 'gray.50' }}
                        >
                          {/* Case Information Column - links to ionmform */}
                          <Td 
                            cursor="pointer" 
                            onClick={() => window.open(`/ionmform?caseid=${caseItem.case_id}`, '_blank')}
                            _hover={{ textDecoration: "underline", color: "blue.500" }}
                          >
                            <VStack align="start" spacing={1}>
                              <HStack>
                                <Badge colorScheme={getStatusColor(caseItem.updated_at)} />
                                <Text fontWeight="medium">
                                  {caseItem.case_name || 'Unnamed Case'}
                                </Text>
                              </HStack>
                              
                              <HStack>
                                <Text fontSize="sm" color="gray.600" fontWeight="medium">
                                  ID: {caseItem.external_patient_id}
                                  {caseItem.external_patient_id_secondary && caseItem.external_patient_id_secondary !== "na" && 
                                    ` (${caseItem.external_patient_id_secondary})`}
                                </Text>
                              </HStack>
                              
                              <Text fontSize="sm" color="gray.600">
                                {caseItem.patient_first_name && caseItem.patient_last_name ? 
                                  `${caseItem.patient_last_name}, ${caseItem.patient_first_name}` : 
                                  'No patient name'}
                              </Text>
                            </VStack>
                          </Td>
                          
                          {/* Procedure Column - no link */}
                          <Td>
                            <Text maxW="250px" isTruncated title={caseItem.external_or_procedure}>
                              {caseItem.external_or_procedure || 'N/A'}
                            </Text>
                          </Td>
                          
                          {/* Dates Column - no link */}
                          <Td>
                            <VStack align="start" spacing={1}>
                              <HStack>
                                <Text fontSize="sm" fontWeight="medium">Procedure:</Text>
                                <Text fontSize="sm">{formatDate(caseItem.procedure_date)}</Text>
                              </HStack>
                              <HStack>
                                <Text fontSize="sm" fontWeight="medium">Updated:</Text>
                                <Text fontSize="sm">{formatDate(caseItem.updated_at)}</Text>
                              </HStack>
                            </VStack>
                          </Td>
                          
                    {/* Coding Status Column - links to coding-review if needs_review */}
                    <Td 
                            cursor={caseItem.coding_status?.status === 'needs_review' ? "pointer" : "default"}
                            onClick={() => {
                              if (caseItem.coding_status?.status === 'needs_review') {
                                window.open(`/coding-review?caseId=${caseItem.case_id}`, '_blank');
                              }
                            }}
                            _hover={caseItem.coding_status?.status === 'needs_review' ? 
                              { textDecoration: "underline", color: "blue.500" } : {}}
                          >
                            {caseItem.coding_status ? (
                              <VStack align="start" spacing={1}>
                                <HStack>
                                  <Badge colorScheme={codingStatusInfo.color}>
                                    {codingStatusInfo.icon && <Box as="span" mr={1}>{codingStatusInfo.icon}</Box>}
                                    {codingStatusInfo.label}
                                  </Badge>
                                  
                                  {caseItem.coding_status.has_cpt_codes && (
                                    <Tag size="sm" colorScheme="blue" variant="subtle">CPT</Tag>
                                  )}
                                  
                                  {caseItem.coding_status.has_icd10_codes && (
                                    <Tag size="sm" colorScheme="purple" variant="subtle">ICD-10</Tag>
                                  )}
                                  
                                  {/* Add Quick Approve button for needs_review status */}
                                  {caseItem.coding_status.status === 'needs_review' && (
                                    <Button
                                      size="xs"
                                      colorScheme="green"
                                      leftIcon={<CheckIcon />}
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent row click
                                        handleQuickApprove(caseItem.case_id, caseItem.coding_status.notes);
                                      }}
                                    >
                                      Approve
                                    </Button>
                                  )}
                                </HStack>
                                
                                {caseItem.coding_status.created_by_user && (
                                  <Text fontSize="xs" color="gray.600">
                                    By: {caseItem.coding_status.created_by_user.name}
                                  </Text>
                                )}
                                
                                {caseItem.coding_status.created_at && (
                                  <Text fontSize="xs" color="gray.600">
                                    {formatDate(caseItem.coding_status.created_at)}
                                  </Text>
                                )}
                                
                                {/* Display notes with markdown support */}
                                {caseItem.coding_status.notes && (
                                  <Box 
                                    mt={2}
                                    p={2}
                                    bg="gray.50"
                                    borderRadius="md"
                                    borderLeft="3px solid"
                                    borderColor="blue.500"
                                    maxW="100%"
                                    overflow="auto"
                                    fontSize="xs"
                                    textAlign="left"
                                  >
                                    <ReactMarkdown>{caseItem.coding_status.notes}</ReactMarkdown>
                                  </Box>
                                )}
                              </VStack>
                            ) : (
                              <Badge colorScheme="gray">Not Started</Badge>
                            )}
                          </Td>

                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            </Box>
            
            {/* Pagination Controls */}
            <Flex justify="center" mt={4}>
              <HStack>
                <IconButton
                  icon={<ChevronLeftIcon />}
                  onClick={goToPrevPage}
                  isDisabled={!pagination.hasPrev || loading}
                  aria-label="Previous page"
                />
                <Text px={4}>
                  Page {pagination.currentPage}
                </Text>
                <IconButton
                  icon={<ChevronRightIcon />}
                  onClick={goToNextPage}
                  isDisabled={!pagination.hasNext || loading}
                  aria-label="Next page"
                />
              </HStack>
            </Flex>
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
}

export default IONMCasesSimple;